import { AxiosError } from "axios";
import { useState } from "react";
import { ApiFactory } from "../../core/config/ApiFactory";
import { useSnackbar } from "../../core/contexts/SnackbarContext";
import { IPlano } from "../../estabelecimento/interfaces/IPlano";
import { IPerfil } from "../../perfis/interfaces/IPerfil";
import {
    IEstabelecimentoOrganizacao,
    IMembroOrganizacao,
    IOrganizacao,
    ISituacaoEstabelecimento,
} from "../interfaces/IOrganizacao";

interface ISalvarAssinaturaPayload {
    planoUuid: string;
    valor?: number;
    diaVencimento?: number;
    dataFimPeriodoTeste?: string;
}

interface IAtualizarAssinaturaPayload {
    valor?: number;
    diaVencimento?: number;
    /** `null` encerra o período de teste; ausente mantém o que está gravado. */
    dataFimPeriodoTeste?: string | null;
}

export const useOrganizacoes = () => {
    const [loading, setLoading] = useState(false);
    const snackbar = useSnackbar();
    const apiUsuarios = ApiFactory.getApi("MS_USUARIOS_ESTABELECIMENTO");
    const apiEstabelecimentos = ApiFactory.getApi("MS_ESTABELECIMENTOS");

    const erro = (mensagem: string, error: unknown) => {
        if (error instanceof AxiosError) {
            const detalhe = error.response?.data?.message;
            snackbar(Array.isArray(detalhe) ? detalhe.join(", ") : (detalhe ?? mensagem), { severity: "error" });
        } else {
            snackbar(mensagem, { severity: "error" });
        }
    };

    const listarMinhas = async (): Promise<IOrganizacao[]> => {
        setLoading(true);
        try {
            const { data } = await apiUsuarios.get("/organizacoes/minhas");
            return data ?? [];
        } catch (error) {
            erro("Ocorreu um erro ao buscar suas organizações", error);
            return [];
        } finally {
            setLoading(false);
        }
    };

    const listarMembros = async (organizacaoUuid: string): Promise<IMembroOrganizacao[]> => {
        setLoading(true);
        try {
            const { data } = await apiUsuarios.get(`/organizacoes/${organizacaoUuid}/membros`);
            return data ?? [];
        } catch (error) {
            erro("Ocorreu um erro ao buscar os membros", error);
            return [];
        } finally {
            setLoading(false);
        }
    };

    const adicionarMembro = async (organizacaoUuid: string, email: string): Promise<boolean> => {
        setLoading(true);
        try {
            await apiUsuarios.post(`/organizacoes/${organizacaoUuid}/membros`, { email });
            snackbar("Membro adicionado com sucesso", { severity: "success" });
            return true;
        } catch (error) {
            erro("Ocorreu um erro ao adicionar o membro", error);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const removerMembro = async (organizacaoUuid: string, usuarioUuid: string): Promise<boolean> => {
        setLoading(true);
        try {
            await apiUsuarios.delete(`/organizacoes/${organizacaoUuid}/membros/${usuarioUuid}`);
            snackbar("Membro removido com sucesso", { severity: "success" });
            return true;
        } catch (error) {
            erro("Ocorreu um erro ao remover o membro", error);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const listarPlanos = async (): Promise<IPlano[]> => {
        setLoading(true);
        try {
            const { data } = await apiEstabelecimentos.get("/planos");
            return data ?? [];
        } catch (error) {
            erro("Ocorreu um erro ao buscar os planos", error);
            return [];
        } finally {
            setLoading(false);
        }
    };

    const listarEstabelecimentos = async (organizacaoUuid: string): Promise<IEstabelecimentoOrganizacao[]> => {
        setLoading(true);
        try {
            const { data } = await apiEstabelecimentos.get(`/organizacoes/${organizacaoUuid}/estabelecimentos`);
            return data ?? [];
        } catch (error) {
            erro("Ocorreu um erro ao buscar os estabelecimentos da organização", error);
            return [];
        } finally {
            setLoading(false);
        }
    };

    // ---- Assinatura e bloqueio de um estabelecimento da organização ----

    const buscarSituacao = async (organizacaoUuid: string, estabelecimentoUuid: string): Promise<ISituacaoEstabelecimento | null> => {
        setLoading(true);
        try {
            const { data } = await apiEstabelecimentos.get(`/organizacoes/${organizacaoUuid}/estabelecimentos/${estabelecimentoUuid}/situacao`);
            return data ?? null;
        } catch (error) {
            erro("Ocorreu um erro ao buscar a situação do estabelecimento", error);
            return null;
        } finally {
            setLoading(false);
        }
    };

    const listarPerfisDoEstabelecimento = async (organizacaoUuid: string, estabelecimentoUuid: string): Promise<{ planoNome: string | null; perfis: IPerfil[] }> => {
        try {
            const { data } = await apiEstabelecimentos.get(`/organizacoes/${organizacaoUuid}/estabelecimentos/${estabelecimentoUuid}/perfis`);
            return data ?? { planoNome: null, perfis: [] };
        } catch (error) {
            erro("Ocorreu um erro ao buscar os perfis do plano", error);
            return { planoNome: null, perfis: [] };
        }
    };

    const bloquearEstabelecimento = async (organizacaoUuid: string, estabelecimentoUuid: string, motivo?: string): Promise<boolean> => {
        setLoading(true);
        try {
            await apiEstabelecimentos.post(`/organizacoes/${organizacaoUuid}/estabelecimentos/${estabelecimentoUuid}/bloquear`, { motivo });
            snackbar("Acesso ao estabelecimento bloqueado", { severity: "success" });
            return true;
        } catch (error) {
            erro("Ocorreu um erro ao bloquear o estabelecimento", error);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const desbloquearEstabelecimento = async (organizacaoUuid: string, estabelecimentoUuid: string): Promise<boolean> => {
        setLoading(true);
        try {
            await apiEstabelecimentos.post(`/organizacoes/${organizacaoUuid}/estabelecimentos/${estabelecimentoUuid}/desbloquear`);
            snackbar("Acesso ao estabelecimento liberado", { severity: "success" });
            return true;
        } catch (error) {
            erro("Ocorreu um erro ao desbloquear o estabelecimento", error);
            return false;
        } finally {
            setLoading(false);
        }
    };

    /** Criar e reativar são a mesma operação: reativar gera uma assinatura nova. */
    const criarAssinatura = async (organizacaoUuid: string, estabelecimentoUuid: string, payload: ISalvarAssinaturaPayload): Promise<boolean> => {
        setLoading(true);
        try {
            await apiEstabelecimentos.post(`/organizacoes/${organizacaoUuid}/estabelecimentos/${estabelecimentoUuid}/assinaturas`, payload);
            snackbar("Assinatura criada com sucesso", { severity: "success" });
            return true;
        } catch (error) {
            erro("Ocorreu um erro ao criar a assinatura", error);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const atualizarAssinatura = async (organizacaoUuid: string, estabelecimentoUuid: string, assinaturaUuid: string, payload: IAtualizarAssinaturaPayload): Promise<boolean> => {
        setLoading(true);
        try {
            await apiEstabelecimentos.put(`/organizacoes/${organizacaoUuid}/estabelecimentos/${estabelecimentoUuid}/assinaturas/${assinaturaUuid}`, payload);
            snackbar("Assinatura atualizada com sucesso", { severity: "success" });
            return true;
        } catch (error) {
            erro("Ocorreu um erro ao atualizar a assinatura", error);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const cancelarAssinatura = async (organizacaoUuid: string, estabelecimentoUuid: string, assinaturaUuid: string): Promise<boolean> => {
        setLoading(true);
        try {
            await apiEstabelecimentos.delete(`/organizacoes/${organizacaoUuid}/estabelecimentos/${estabelecimentoUuid}/assinaturas/${assinaturaUuid}`);
            snackbar("Assinatura cancelada", { severity: "success" });
            return true;
        } catch (error) {
            erro("Ocorreu um erro ao cancelar a assinatura", error);
            return false;
        } finally {
            setLoading(false);
        }
    };

    // Operações de super-admin (Space Food)
    const criarOrganizacao = async (dados: { nome: string; cnpj?: string; razaoSocial?: string }): Promise<IOrganizacao | null> => {
        setLoading(true);
        try {
            const { data } = await apiEstabelecimentos.post("/organizacoes", dados);
            snackbar("Organização criada com sucesso", { severity: "success" });
            return data?.organizacao ?? null;
        } catch (error) {
            erro("Ocorreu um erro ao criar a organização", error);
            return null;
        } finally {
            setLoading(false);
        }
    };

    const listarOrganizacoes = async (): Promise<IOrganizacao[]> => {
        setLoading(true);
        try {
            const { data } = await apiEstabelecimentos.get("/organizacoes");
            return data ?? [];
        } catch (error) {
            erro("Ocorreu um erro ao buscar as organizações", error);
            return [];
        } finally {
            setLoading(false);
        }
    };

    const vincularEstabelecimento = async (organizacaoUuid: string, estabelecimentoUuid: string): Promise<boolean> => {
        setLoading(true);
        try {
            await apiEstabelecimentos.post(`/organizacoes/${organizacaoUuid}/estabelecimentos/${estabelecimentoUuid}`);
            snackbar("Estabelecimento vinculado à organização", { severity: "success" });
            return true;
        } catch (error) {
            erro("Ocorreu um erro ao vincular o estabelecimento", error);
            return false;
        } finally {
            setLoading(false);
        }
    };

    return {
        loading,
        listarMinhas,
        listarMembros,
        adicionarMembro,
        removerMembro,
        listarPlanos,
        listarEstabelecimentos,
        buscarSituacao,
        listarPerfisDoEstabelecimento,
        bloquearEstabelecimento,
        desbloquearEstabelecimento,
        criarAssinatura,
        atualizarAssinatura,
        cancelarAssinatura,
        criarOrganizacao,
        listarOrganizacoes,
        vincularEstabelecimento,
    };
};
